/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useEffect, useRef, useState } from 'react'
import { SearchIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useSearch } from '@/context/search-provider'

type HeaderSearchProps = {
  placeholder?: string
}

/**
 * Morph-in-place top-bar search (noya skin).
 *
 * DEFAULT (collapsed): a single compact icon button — the search SVG — that
 * takes no extra horizontal space.
 *
 * ON CLICK (expanded): the button morphs into a pill-shaped input that grows
 * to the LEFT of the icon with an animated width transition (0 → ~220 px).
 * The search SVG stays anchored on the RIGHT side as a trailing icon inside
 * the expanded pill.  The input is auto-focused.
 *
 * COLLAPSE: the pill retracts (width → 0) when:
 *   • the input loses focus AND the field is empty, OR
 *   • the user presses Escape.
 * If text is present on blur the field stays open so the user can return.
 *
 * Pressing Enter hands off to the global command-palette (useSearch().setOpen)
 * so existing search functionality is fully preserved.
 */
export function HeaderSearch({ placeholder }: HeaderSearchProps) {
  const { t } = useTranslation()
  const { setOpen } = useSearch()
  const [expanded, setExpanded] = useState(false)
  const [value, setValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const resolvedPlaceholder = placeholder ?? t('Search')

  // Auto-focus the input on the next animation frame so the width transition
  // has already started and the element is measurable.
  useEffect(() => {
    if (expanded) {
      const id = requestAnimationFrame(() => inputRef.current?.focus())
      return () => cancelAnimationFrame(id)
    }
  }, [expanded])

  const collapse = () => {
    setExpanded(false)
    setValue('')
  }

  const handleIconClick = () => {
    if (expanded) {
      // Clicking the trailing icon while open collapses the field.
      collapse()
    } else {
      setExpanded(true)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      collapse()
      return
    }
    if (event.key === 'Enter') {
      // Hand off to the global command-palette search.
      setOpen(true)
    }
  }

  const handleBlur = () => {
    // Only auto-collapse when the user leaves the field empty.
    if (value.trim() === '') {
      setExpanded(false)
    }
  }

  return (
    /*
     * The outer element is always exactly as wide as the icon button (size-8)
     * when collapsed. When expanded it grows by `max-w-*` via CSS transition so
     * that the icon button stays glued to the right edge at all times —
     * producing the morph-in-place illusion.
     */
    <div
      className={cn(
        'relative flex h-8 items-center justify-end',
        'overflow-hidden rounded-md',
        'transition-[max-width,background-color,border-color] duration-300 ease-out',
        expanded
          ? 'bg-card border-border max-w-[220px] border sm:max-w-[240px] lg:max-w-[280px]'
          : 'max-w-8 border-transparent bg-transparent'
      )}
    >
      {/* Text input — sits to the left of the trailing icon; fades in/out */}
      <input
        ref={inputRef}
        type='search'
        value={value}
        tabIndex={expanded ? 0 : -1}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={resolvedPlaceholder}
        aria-label={resolvedPlaceholder}
        aria-hidden={!expanded}
        className={cn(
          'text-foreground placeholder:text-muted-foreground/60',
          'h-full min-w-0 flex-1 border-none bg-transparent ps-2.5 text-sm outline-none',
          'transition-opacity duration-200',
          expanded ? 'opacity-100' : 'pointer-events-none opacity-0 w-0'
        )}
      />

      {/* Trailing search icon — always visible; acts as the toggle button */}
      <button
        type='button'
        onClick={handleIconClick}
        aria-label={resolvedPlaceholder}
        aria-expanded={expanded}
        className={cn(
          'text-muted-foreground',
          'flex size-8 shrink-0 items-center justify-center rounded-md transition-colors',
          // When collapsed the button fills the whole container → plain icon button.
          // When expanded it sits as a flushed trailing icon inside the pill.
          expanded
            ? 'hover:text-foreground'
            : 'hover:bg-accent hover:text-accent-foreground'
        )}
      >
        <SearchIcon aria-hidden='true' size={17} />
      </button>
    </div>
  )
}
